import React, { useRef } from 'react';

import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CoreLink, RecursiveMenu } from '@uncinc/react-drupal-core';
import type { MenuItemLinkElementProps } from '@uncinc/react-drupal-core/dist/Components/RecursiveMenu/types';
import { Button, useWindowDimensions } from '@uncinc/uncinc-react-kitchen-sink';

import { SCREEN_TABLET } from '../../config/constants';
import useCurrentUser from '../../hooks/useCurrentUser';
import useLoggedInStatus from '../../hooks/useLoggedInstatus';
import { ROUTE_ACCOUNT_EDIT, ROUTE_ACCOUNT_LOGIN, ROUTE_USER_LOGOUT } from '../../config/routes';

import './style.scss';

const AccountNavLinkElement: React.FC<MenuItemLinkElementProps> = ({
  title,
  url,
  onClickFunction,
}) => {
  // Logout requires a hard navigation to let the backend handle session teardown.
  if (url === ROUTE_USER_LOGOUT) {
    return (
      <a href={ROUTE_USER_LOGOUT} className="RecursiveMenu__title">
        {title}
      </a>
    );
  }

  return (
    <CoreLink
      href={url}
      className="RecursiveMenu__title"
      onClick={onClickFunction}
    >
      {title}
    </CoreLink>
  );
};

const AccountButton: React.FC = () => {
  const accountNavWrapperRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const { t } = useTranslation();
  const user = useCurrentUser();
  const { isLoggedIn } = useLoggedInStatus();
  const { width } = useWindowDimensions();

  const { profile } = user ?? {};
  const {
    field_first_name: fieldFirstName = null,
    field_last_name: fieldLastName = null,
    field_infix_name: fieldInfixName = null,
  } = profile ?? {};

  const accountNavVisible = useRef(false);

  const toggleClasses = () => {
    accountNavWrapperRef?.current?.classList.toggle('menuIsOpen');
    accountNavVisible.current = !accountNavVisible.current;
  };

  const handleAccountButtonClick = () => {
    if (isLoggedIn === null) return;
    if (isLoggedIn !== false) toggleClasses();
    else history.push(ROUTE_ACCOUNT_LOGIN);
  };

  return (
    <div
      className="AccountNav"
      ref={accountNavWrapperRef}
      onBlur={() => {
        if (width > SCREEN_TABLET && accountNavVisible.current) setTimeout(toggleClasses, 120);
      }}
    >
      <div className="AccountButton">
        <Button
          primary={false}
          onClick={handleAccountButtonClick}
          className="AccountButton__desktop"
        >
          {
            fieldFirstName ? (
              <span className="AccountButton__initials">
                {fieldFirstName[0]!.value[0]}{fieldLastName && fieldLastName[0]
                  ? fieldLastName[0].value[0] : ''}
              </span>
            )
              : <i className="icon icon-user" />
          }
        </Button>
        <Button
          primary={false}
          className="AccountButton__mobile"
          href={user ? ROUTE_ACCOUNT_EDIT : ROUTE_ACCOUNT_LOGIN}
        >
          {
            fieldFirstName ? (
              <span className="AccountButton__initials">
                {fieldFirstName[0]!.value[0]}{fieldLastName && fieldLastName[0]
                  ? fieldLastName[0].value[0] : ''}
              </span>
            )
              : <i className="icon icon-user" />
          }
        </Button>
        <div
          className="AccountButton__nav"
          aria-expanded={accountNavVisible.current}
        >
          {
            fieldFirstName && (
              <div className="AccountButton__nav__name">
                <span>
                  {fieldFirstName[0]!.value}
                  {fieldInfixName && fieldInfixName[0] ? ` ${fieldInfixName[0].value}` : ''}
                  {fieldLastName && fieldLastName[0] ? ` ${fieldLastName[0].value}` : ''}
                </span>
              </div>
            )
          }
          <RecursiveMenu
            menuId="account"
            MenuItemLinkElement={AccountNavLinkElement}
            navProps={{ 'aria-label': t('mijn_vnl.navigation_aria_label') }}
          />
        </div>
      </div>
    </div>
  );
};

export default React.memo(AccountButton);
